import { type GarmentGeometry, FidelityStates, FidelityStateProgresses } from '@seddi/scene';
import { LoadingManager } from '@seddi/three';
import { type TSceneEx, loadGarmentGeometry } from '@seddi/ui-three-viewer';

import { decodeInPlace } from '../domain/scene/decodeFromJson';
import type { TSceneJsonDto } from '../domain/scene/sceneJsonDto';
import { computeVertexNormals } from './computeNormals';

const fetchAndEmbedHiResGeometry = async (scene: TSceneEx) => {
  const {
    fidelityState: { progress, state },
  } = scene;

  const hasHighRes =
    (state === FidelityStates.TrueSeams && progress === FidelityStateProgresses.Done) ||
    state === FidelityStates.DigitalTwin;
  if (!hasHighRes) {
    return;
  }

  const { children } = scene;
  const geometryEntity = children.find(e => Boolean(e.components.garmentGeometry));
  if (!geometryEntity) {
    throw Error('Garment geometry not found');
  }
  if (!geometryEntity.components.garmentGeometry!.surfaceUrl) {
    throw Error('Garment geometry component surface URL not found');
  }

  const manager = new LoadingManager();
  const garmentGeometry = await loadGarmentGeometry(
    geometryEntity.components.garmentGeometry!.surfaceUrl,
    scene.children,
    manager,
  );

  for (const piece of garmentGeometry.pieces) {
    computeVertexNormals(piece);
  }

  scene.hiResGeometry = garmentGeometry;
};

/**
 * Import scene data.
 * @param sceneDto Scene DTO.
 */
export const importScene = async (
  sceneDto: TSceneJsonDto,
  localHiResGeometry?: GarmentGeometry | undefined,
): Promise<TSceneEx> => {
  // Decode binary buffers.
  const scene = decodeInPlace(sceneDto);

  // Remove unnecesary entities.
  scene.children = scene.children.filter(e => {
    if (e.components.avatar || e.components.mesh) {
      return false;
    }
    return true;
  });

  // Embed hi-res geometry.
  if (localHiResGeometry) {
    (scene as TSceneEx).hiResGeometry = localHiResGeometry;
  } else {
    await fetchAndEmbedHiResGeometry(scene);
  }

  return scene;
};
